import React, { useState, useContext, useEffect } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const EditStateForm = ({ state, onCancel, onUpdated }) => {
  const [formData, setFormData] = useState({ ...state });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);
  const isPrimary = user?.is_primary;

  useEffect(() => {
    // Track if form has changes
    const changed =
      formData.state_name !== state.state_name ||
      formData.state_code !== state.state_code;
    setHasChanges(changed);
  }, [formData, state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "state_code") {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.state_name.trim()) {
      setError("State name is required");
      return;
    }

    if (!formData.state_code.trim()) {
      setError("State code is required");
      return;
    }

    if (!/^[A-Z]{2}$/.test(formData.state_code)) {
      setError("State code must be exactly 2 letters");
      return;
    }

    if (!hasChanges) {
      setError("No changes to save");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      await secureAxios.put(`${apiBase}/api/states/${formData.state_id}`, {
        state_name: formData.state_name.trim(),
        state_code: formData.state_code.trim(),
      });

      alert("✅ State updated successfully!");
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("❌ Error updating state", err);
      setError(err.response?.data?.error || "Failed to update state");
    } finally {
      setLoading(false);
    }
  };

  // Check authorization
  if (!isPrimary) {
    return (
      <div className="alert alert-warning" role="alert">
        Only primary tenant users can edit states.
      </div>
    );
  }

  return (
    <div>
      <h4>Edit State</h4>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3 border-top pt-3 mt-3">
        <div className="col-md-8">
          <label htmlFor="edit-state-name" className="form-label">
            State Name <span className="text-danger">*</span>
          </label>
          <input
            id="edit-state-name"
            className="form-control"
            name="state_name"
            value={formData.state_name}
            onChange={handleChange}
            required
            maxLength={100}
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label htmlFor="edit-state-code" className="form-label">
            State Code <span className="text-danger">*</span>
          </label>
          <input
            id="edit-state-code"
            className="form-control text-uppercase"
            name="state_code"
            value={formData.state_code}
            onChange={handleChange}
            required
            maxLength={2}
            disabled={loading}
          />
          <div className="form-text">2-letter abbreviation</div>
        </div>

        {/* Audit Information */}
        {(formData.created_at || formData.updated_at) && (
          <div className="col-12">
            <div className="card bg-light">
              <div className="card-body py-2">
                <small className="text-muted">
                  <strong>Audit Trail:</strong>
                  <br />
                  Created: {new Date(formData.created_at).toLocaleString()}
                  {formData.updated_at &&
                    formData.updated_at !== formData.created_at && (
                      <>
                        <br />
                        Last updated:{" "}
                        {new Date(formData.updated_at).toLocaleString()}
                      </>
                    )}
                </small>
              </div>
            </div>
          </div>
        )}

        <div className="col-12 d-flex justify-content-between">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <div>
            <button
              className="btn btn-outline-secondary me-2"
              type="button"
              onClick={() => setFormData({ ...state })}
              disabled={loading || !hasChanges}
            >
              Reset
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading || !hasChanges}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Global Impact Notice */}
      <div className="alert alert-warning mt-3" role="alert">
        <i className="bi bi-globe"></i> <strong>Global Impact:</strong>
        Updating this state will affect all tenants using this state in their
        addresses.
      </div>
    </div>
  );
};

export default EditStateForm;
